import { useState } from "react"
import { Box, Button, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Bound } from "lib/@components/binding/Bound"
import { ListItemBox } from "lib/@components/ListItemBox"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { StandardSelector } from "lib/@components/combos/standard-selector"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useReferenceState } from "lib/@hooks/use-reference"
import { emptyArray } from "lib/empty-array"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import Iconify from "minimals-template/components/Iconify"
import { navigate } from "lib/routes/navigate"
import { scenarios } from "routes/scenarios/controller/scenarios"
import { deleteScenario } from "routes/scenarios/controller/delete-scenario"
import { ScenarioRow } from "./scenario-row"
import { ScenarioTableHeader } from "./scenario-table-header"
import { ScenariosEmpty } from "./scenarios-empty"
import { useDemand } from "lib/authorization/use-demand"

const sortOptions = [
    { value: { sort: "created", descending: true }, label: "Latest" },
    { value: { sort: "created", descending: false }, label: "Oldest" },
    { value: { sort: "name", descending: false }, label: "Name" },
    { value: { sort: "name", descending: true }, label: "Name (desc)" },
    { value: { sort: "modified", descending: true }, label: "Modified" },
    { value: { sort: "modified", descending: false }, label: "Modified (asc)" },
]

export function ScenariosTab() {
    const canManageOptimiser = useDemand("manageScenarios")
    const [skip, setSkip] = useState(0)
    const [selectedIds, setSelectedIds] = useState(() => new Set())
    const [removedIds, setRemovedIds] = useState(() => new Set())
    const [search] = useSearchParam("search")
    const [storedSortMode, setSortMode] = useReferenceState("scenariosSort", sortOptions[0].value)
    const sortMode = (
        sortOptions.find(
            (f) => f.value.sort === storedSortMode?.sort && f.value.descending === storedSortMode?.descending
        ) ?? sortOptions[0]
    )?.value
    const resultPair = scenarios.useResults.withRefetch({
        skip,
        take: 5,
        search,
        ...sortMode,
    })
    const [result, refetchScenarios] = resultPair ?? []
    const rawItems = result?.items ?? emptyArray
    const items = rawItems === emptyArray ? emptyArray : rawItems.filter(isNotRemoved)
    const removedOnPageCount = rawItems === emptyArray ? 0 : rawItems.filter(isRemoved).length
    const totalCount = Math.max((result?.totalCount ?? 0) - removedOnPageCount, 0)

    return (
        <If value={items !== emptyArray}>
            <If value={totalCount > 0 || search}>
                <Stack data-cy="scenarios-list" spacing={2} sx={{ pt: 1.5 }}>
                    <Typography variant="h5" sx={{ fontSize: 18 }}>
                        Scenarios
                    </Typography>
                    <ListItemBox spacing={1.5} sx={{ alignItems: "center" }}>
                        <If value={canManageOptimiser}>
                            <Button
                                variant="contained"
                                startIcon={<Iconify icon="mdi:plus" />}
                                onClick={handleCreate}
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                Create Scenario
                            </Button>
                        </If>
                        <Box flex={1} />
                        <StandardSelector
                            sortTitle="Sort:"
                            options={sortOptions}
                            value={sortMode}
                            onChange={handleSortChange}
                            sx={{
                                fontSize: "85%",
                                color: "text.primary",
                                whiteSpace: "nowrap",
                                textTransform: "none",
                                fontWeight: "normal",
                            }}
                        />
                    </ListItemBox>
                    <SearchBoxUsingSearchParam autoFocus={false} placeholder="Search..." sx={{ py: 0, m: 0 }} />
                    <Bound
                        selectedIds={selectedIds}
                        onToggleSelect={handleToggleSelect}
                        onSelectAll={handleSelectAll}
                        pageItems={items}
                        canManageOptimiser={canManageOptimiser}
                    >
                        <If value={canManageOptimiser && selectedIds.size > 0}>
                            <ListItemBox
                                sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                    {selectedIds.size} selected
                                </Typography>
                                <Box flex={1} />
                                <Button
                                    size="small"
                                    variant="text"
                                    color="primary"
                                    onClick={handleClearSelection}
                                    sx={{ mr: 1 }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    size="small"
                                    variant="text"
                                    color="error"
                                    startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                                    onClick={handleBulkDelete}
                                >
                                    Delete
                                </Button>
                            </ListItemBox>
                        </If>
                        <Stack spacing={0}>
                            <ScenarioTableHeader />
                            <RequestPageRepeat
                                bind
                                refreshKey={`${search}-${sortMode?.sort}-${sortMode?.descending}`}
                                total={totalCount}
                                list={items}
                                setSkip={setSkip}
                                pageSize={5}
                                item={<ScenarioRow onAfterDelete={handleAfterDelete} />}
                            />
                        </Stack>
                    </Bound>
                </Stack>
                <Else>
                    <ScenariosEmpty canManageOptimiser={canManageOptimiser} />
                </Else>
            </If>
            <Else>
                <LoadingScreen description="Retrieving Scenarios" />
            </Else>
        </If>
    )

    function handleToggleSelect(id) {
        if (!canManageOptimiser) return
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    function handleSelectAll() {
        if (!canManageOptimiser) return
        const allSelected = items.length > 0 && items.every(isSelected)
        if (allSelected) {
            setSelectedIds(new Set())
        } else {
            setSelectedIds(new Set(items.map((i) => i.id)))
        }
    }

    function handleClearSelection() {
        setSelectedIds(new Set())
    }

    function isSelected(item) {
        return selectedIds.has(item.id)
    }

    async function handleBulkDelete() {
        const count = selectedIds.size
        const ok = await confirm(
            `Move ${count} scenario${count === 1 ? "" : "s"} to the Recycle Bin? ${
                count === 1 ? "It" : "They"
            } can be restored later.`,
            "Delete Scenarios"
        )
        if (!ok) return
        await busyWhile(
            async () => {
                for (const id of selectedIds) {
                    await deleteScenario({ id })
                }
            },
            `Deleting ${count} scenario${count === 1 ? "" : "s"}...`
        )
        markRemoved(selectedIds)
        setSelectedIds(new Set())
        refetchScenarios?.()
        successSnackbar(`${count} scenario${count === 1 ? "" : "s"} moved to the Recycle Bin`)
    }

    function handleAfterDelete(id) {
        markRemoved([id])
        refetchScenarios?.()
    }

    function markRemoved(ids) {
        setRemovedIds((current) => {
            const next = new Set(current)
            for (const id of ids) {
                next.add(id)
            }
            return next
        })
    }

    function isRemoved(item) {
        return removedIds.has(item.id)
    }

    function isNotRemoved(item) {
        return !isRemoved(item)
    }

    function handleSortChange(value) {
        setSortMode(value)
        setSkip(0)
    }

    function handleCreate() {
        navigate("/app/optimiser/scenarios/create")
    }
}
